/**
 * Enterprise Vendor Portal Dashboard
 * 
 * A comprehensive dashboard for vendor management with:
 * - Executive overview metrics
 * - Quick actions
 * - Recent activity feed
 * - Compliance alerts
 * - Performance summaries
 */

import { useState, useEffect, useMemo } from 'react';
import {
    LayoutDashboard,
    Building2,
    Users,
    Clock,
    CheckCircle,
    XCircle,
    AlertTriangle,
    TrendingUp,
    TrendingDown,
    DollarSign,
    Star,
    Shield,
    FileText,
    Send,
    MessageSquare,
    Calendar,
    Bell,
    ExternalLink,
    ChevronRight,
    RefreshCw,
    Loader,
    AlertCircle,
    Award,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { vendorService } from '../utils/vendors/vendorService';
import { vendorInvitationService } from '../utils/vendors/invitationService';
import { Vendor } from '../types/vendor';
import { formatDistanceToNow } from 'date-fns';

interface VendorPortalDashboardProps {
    user: {
        id: string;
        name: string;
        email: string;
        role: string;
    };
    company: {
        id: string;
        name: string;
    };
    onNavigate?: (section: string) => void;
}

interface DashboardMetrics {
    totalVendors: number;
    activeVendors: number;
    pendingApproval: number;
    pendingInvitations: number;
    suspendedVendors: number;
    avgRating: number;
    complianceScore: number;
    monthlySpend: number;
    monthlySpendChange: number;
    expiringDocuments: number;
    openTickets: number;
    avgResponseTime: string;
}

interface RecentActivity {
    id: string;
    type: 'invitation_sent' | 'vendor_approved' | 'vendor_rejected' | 'document_uploaded' | 'invoice_submitted' | 'ticket_completed';
    description: string;
    timestamp: Date;
    vendorName?: string;
    userId?: string;
    userName?: string;
}

export default function VendorPortalDashboard({
    user,
    company,
    onNavigate,
}: VendorPortalDashboardProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [invitationStats, setInvitationStats] = useState({ total: 0, sent: 0, accepted: 0, completed: 0, expired: 0, declined: 0 });
    const [recentActivity] = useState<RecentActivity[]>([]);

    useEffect(() => {
        loadDashboardData();
    }, [company.id]);

    const loadDashboardData = async () => {
        try {
            setIsLoading(true);

            // Load vendors
            const vendorData = await vendorService.getVendors();
            setVendors(vendorData);

            // Load invitation stats
            const stats = await vendorInvitationService.getStats(company.id);
            setInvitationStats(stats);

        } catch (error) {
            console.error('Error loading dashboard data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await loadDashboardData();
        setIsRefreshing(false);
        toast.success('Dashboard refreshed');
    };

    // Calculate metrics
    const metrics: DashboardMetrics = useMemo(() => {
        const active = vendors.filter(v => v.status === 'active');
        const pending = vendors.filter(v => v.status === 'pending_approval');
        const suspended = vendors.filter(v => v.status === 'suspended');

        // Calculate average rating
        const vendorsWithRating = vendors.filter(v => v.rating?.overall);
        const avgRating = vendorsWithRating.length > 0
            ? vendorsWithRating.reduce((acc, v) => acc + (v.rating?.overall || 0), 0) / vendorsWithRating.length
            : 0;

        // Calculate compliance score (vendors with valid insurance / total active)
        const compliantVendors = active.filter(v =>
            v.generalLiability &&
            v.workersCompensation &&
            (!v.generalLiability.expirationDate || new Date(v.generalLiability.expirationDate) > new Date()) &&
            (!v.workersCompensation.expirationDate || new Date(v.workersCompensation.expirationDate) > new Date())
        );
        const complianceScore = active.length > 0
            ? Math.round((compliantVendors.length / active.length) * 100)
            : 100;

        // Count expiring documents (within 30 days)
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
        const expiringDocuments = active.filter(v => {
            const glExpires = v.generalLiability?.expirationDate ? new Date(v.generalLiability.expirationDate) : null;
            const wcExpires = v.workersCompensation?.expirationDate ? new Date(v.workersCompensation.expirationDate) : null;
            return (glExpires && glExpires < thirtyDaysFromNow) || (wcExpires && wcExpires < thirtyDaysFromNow);
        }).length;

        // Calculate total revenue (monthly spend)
        const monthlySpend = active.reduce((acc, v) => acc + (v.performanceMetrics?.totalRevenue || 0), 0);

        return {
            totalVendors: vendors.length,
            activeVendors: active.length,
            pendingApproval: pending.length,
            pendingInvitations: invitationStats.sent + invitationStats.accepted,
            suspendedVendors: suspended.length,
            avgRating,
            complianceScore,
            monthlySpend,
            monthlySpendChange: 12.5, // Placeholder - would calculate from historical data
            expiringDocuments,
            openTickets: 0, // Would come from ticket service
            avgResponseTime: '2.4h', // Would come from ticket service
        };
    }, [vendors, invitationStats]);

    // Vendors needing attention
    const vendorsNeedingAttention = useMemo(() => {
        const now = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

        return vendors.filter(v => {
            if (v.status === 'pending_approval') return true;

            // Check for expiring insurance
            const glExpires = v.generalLiability?.expirationDate ? new Date(v.generalLiability.expirationDate) : null;
            const wcExpires = v.workersCompensation?.expirationDate ? new Date(v.workersCompensation.expirationDate) : null;

            if (glExpires && glExpires < thirtyDaysFromNow) return true;
            if (wcExpires && wcExpires < thirtyDaysFromNow) return true;

            return false;
        }).slice(0, 5);
    }, [vendors]);

    // Top performing vendors
    const topVendors = useMemo(() => {
        return vendors
            .filter(v => v.status === 'active' && v.rating?.overall)
            .sort((a, b) => (b.rating?.overall || 0) - (a.rating?.overall || 0))
            .slice(0, 5);
    }, [vendors]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center space-x-3">
                        <LayoutDashboard className="h-8 w-8 text-blue-600" />
                        <span>Vendor Portal</span>
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        Enterprise vendor management for {company.name}
                    </p>
                </div>
                <Button
                    onClick={handleRefresh}
                    variant="outline"
                    disabled={isRefreshing}
                    className="flex items-center space-x-2"
                >
                    <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    <span>Refresh</span>
                </Button>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {/* Total Vendors */}
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 dark:from-blue-900/40 dark:to-blue-900/10 dark:border-blue-800">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <Building2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                            <Badge className="bg-blue-600 text-white">{metrics.totalVendors}</Badge>
                        </div>
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mt-2">Total Vendors</p>
                    </CardContent>
                </Card>

                {/* Active */}
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 dark:from-green-900/40 dark:to-green-900/10 dark:border-green-800">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                            <Badge className="bg-green-600 text-white">{metrics.activeVendors}</Badge>
                        </div>
                        <p className="text-sm font-medium text-green-900 dark:text-green-100 mt-2">Active</p>
                    </CardContent>
                </Card>

                {/* Pending Approval */}
                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 dark:from-orange-900/40 dark:to-orange-900/10 dark:border-orange-800 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => onNavigate?.('pending_approval')}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <Clock className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                            <Badge className="bg-orange-600 text-white">{metrics.pendingApproval}</Badge>
                        </div>
                        <p className="text-sm font-medium text-orange-900 dark:text-orange-100 mt-2">Pending Approval</p>
                    </CardContent>
                </Card>

                {/* Pending Invitations */}
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 dark:from-purple-900/40 dark:to-purple-900/10 dark:border-purple-800 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => onNavigate?.('pending_invites')}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <Send className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                            <Badge className="bg-purple-600 text-white">{metrics.pendingInvitations}</Badge>
                        </div>
                        <p className="text-sm font-medium text-purple-900 dark:text-purple-100 mt-2">Invitations</p>
                    </CardContent>
                </Card>

                {/* Compliance Score */}
                <Card className={`bg-gradient-to-br ${metrics.complianceScore >= 80 ? 'from-emerald-50 to-emerald-100 border-emerald-200 dark:from-emerald-900/40 dark:to-emerald-900/10 dark:border-emerald-800' : 'from-amber-50 to-amber-100 border-amber-200 dark:from-amber-900/40 dark:to-amber-900/10 dark:border-amber-800'}`}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <Shield className={`h-8 w-8 ${metrics.complianceScore >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`} />
                            <Badge className={`${metrics.complianceScore >= 80 ? 'bg-emerald-600' : 'bg-amber-600'} text-white`}>
                                {metrics.complianceScore}%
                            </Badge>
                        </div>
                        <p className={`text-sm font-medium mt-2 ${metrics.complianceScore >= 80 ? 'text-emerald-900 dark:text-emerald-100' : 'text-amber-900 dark:text-amber-100'}`}>
                            Compliance
                        </p>
                    </CardContent>
                </Card>

                {/* Average Rating */}
                <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200 dark:from-yellow-900/40 dark:to-yellow-900/10 dark:border-yellow-800">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <Star className="h-8 w-8 text-yellow-600 fill-yellow-400" />
                            <Badge className="bg-yellow-600 text-white">
                                {metrics.avgRating > 0 ? metrics.avgRating.toFixed(1) : 'N/A'}
                            </Badge>
                        </div>
                        <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mt-2">Avg Rating</p>
                    </CardContent>
                </Card>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Actions & Alerts */}
                <div className="space-y-6">
                    {/* Quick Actions */}
                    <Card className="dark:bg-slate-900 dark:border-slate-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center space-x-2">
                                <Bell className="h-5 w-5 text-blue-600" />
                                <span>Quick Actions</span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <Button
                                onClick={() => onNavigate?.('invite')}
                                className="w-full justify-start bg-blue-600 hover:bg-blue-700"
                            >
                                <Send className="h-4 w-4 mr-2" />
                                Invite New Vendor
                            </Button>
                            <Button
                                onClick={() => onNavigate?.('bulk-import')}
                                variant="outline"
                                className="w-full justify-start"
                            >
                                <Users className="h-4 w-4 mr-2" />
                                Bulk Import Vendors
                            </Button>
                            <Button
                                onClick={() => onNavigate?.('compliance')}
                                variant="outline"
                                className="w-full justify-start"
                            >
                                <Shield className="h-4 w-4 mr-2" />
                                Compliance Report
                            </Button>
                            <Button
                                onClick={() => onNavigate?.('analytics')}
                                variant="outline"
                                className="w-full justify-start"
                            >
                                <TrendingUp className="h-4 w-4 mr-2" />
                                Performance Analytics
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Compliance Alerts */}
                    {metrics.expiringDocuments > 0 && (
                        <Card className="border-amber-200 bg-amber-50">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center space-x-2 text-amber-800">
                                    <AlertTriangle className="h-5 w-5" />
                                    <span>Compliance Alerts</span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                                    <div>
                                        <p className="font-medium text-amber-900">
                                            {metrics.expiringDocuments} Document{metrics.expiringDocuments !== 1 ? 's' : ''} Expiring Soon
                                        </p>
                                        <p className="text-sm text-amber-700">
                                            Insurance certificates expiring within 30 days
                                        </p>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-amber-300 text-amber-700 hover:bg-amber-100"
                                        onClick={() => onNavigate?.('compliance')}
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Performance Summary */}
                    <Card className="dark:bg-slate-900 dark:border-slate-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center space-x-2">
                                <TrendingUp className="h-5 w-5 text-green-600" />
                                <span>Performance Summary</span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600 dark:text-gray-400">Compliance Rate</span>
                                    <span className="font-medium dark:text-white">{metrics.complianceScore}%</span>
                                </div>
                                <Progress value={metrics.complianceScore} className="h-2" />
                            </div>
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600 dark:text-gray-400">Active Vendor Ratio</span>
                                    <span className="font-medium dark:text-white">
                                        {metrics.totalVendors > 0
                                            ? Math.round((metrics.activeVendors / metrics.totalVendors) * 100)
                                            : 0}%
                                    </span>
                                </div>
                                <Progress
                                    value={metrics.totalVendors > 0 ? (metrics.activeVendors / metrics.totalVendors) * 100 : 0}
                                    className="h-2"
                                />
                            </div>
                            <div className="pt-2 border-t">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">Avg Response Time</span>
                                    <span className="font-semibold text-green-600">{metrics.avgResponseTime}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Center Column - Vendors Needing Attention */}
                <div className="space-y-6">
                    <Card className="dark:bg-slate-900 dark:border-slate-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <AlertCircle className="h-5 w-5 text-orange-600" />
                                    <span>Needs Attention</span>
                                </div>
                                <Badge variant="outline" className="text-orange-600 border-orange-300">
                                    {vendorsNeedingAttention.length}
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {vendorsNeedingAttention.length === 0 ? (
                                <div className="text-center py-8">
                                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                                    <p className="text-gray-600">All vendors are up to date!</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {vendorsNeedingAttention.map(vendor => (
                                        <div
                                            key={vendor.id}
                                            className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer"
                                            onClick={() => onNavigate?.(`vendor/${vendor.id}`)}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <p className="font-medium text-gray-900">{vendor.name}</p>
                                                    <p className="text-sm text-gray-600">{vendor.contactName}</p>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    {vendor.status === 'pending_approval' && (
                                                        <Badge className="bg-orange-100 text-orange-700">
                                                            <Clock className="h-3 w-3 mr-1" />
                                                            Pending
                                                        </Badge>
                                                    )}
                                                    {vendor.generalLiability?.expirationDate &&
                                                        new Date(vendor.generalLiability.expirationDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                                                            <Badge className="bg-amber-100 text-amber-700">
                                                                <Shield className="h-3 w-3 mr-1" />
                                                                Expiring
                                                            </Badge>
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {vendorsNeedingAttention.length > 0 && (
                                        <Button
                                            variant="outline"
                                            className="w-full"
                                            onClick={() => onNavigate?.('all')}
                                        >
                                            View All Vendors
                                            <ChevronRight className="h-4 w-4 ml-2" />
                                        </Button>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Invitation Pipeline */}
                    <Card className="dark:bg-slate-900 dark:border-slate-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center space-x-2">
                                <Send className="h-5 w-5 text-purple-600" />
                                <span>Invitation Pipeline</span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                                    <span className="text-blue-900">Sent</span>
                                    <Badge className="bg-blue-600 text-white">{invitationStats.sent}</Badge>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                    <span className="text-green-900">Accepted</span>
                                    <Badge className="bg-green-600 text-white">{invitationStats.accepted}</Badge>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                                    <span className="text-emerald-900">Completed</span>
                                    <Badge className="bg-emerald-600 text-white">{invitationStats.completed}</Badge>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                                    <span className="text-red-900">Expired/Declined</span>
                                    <Badge className="bg-red-600 text-white">
                                        {invitationStats.expired + invitationStats.declined}
                                    </Badge>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Right Column - Top Vendors & Activity */}
                <div className="space-y-6">
                    {/* Top Performing Vendors */}
                    <Card className="dark:bg-slate-900 dark:border-slate-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center space-x-2">
                                <Award className="h-5 w-5 text-yellow-600" />
                                <span>Top Performers</span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {topVendors.length === 0 ? (
                                <div className="text-center py-8">
                                    <Star className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                    <p className="text-gray-600">No rated vendors yet</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {topVendors.map((vendor, index) => (
                                        <div
                                            key={vendor.id}
                                            className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                                            onClick={() => onNavigate?.(`vendor/${vendor.id}`)}
                                        >
                                            <div className={`
                        h-8 w-8 rounded-full flex items-center justify-center font-bold text-white
                        ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'}
                      `}>
                                                {index + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 truncate">{vendor.name}</p>
                                                <p className="text-sm text-gray-600">
                                                    {vendor.performanceMetrics.jobsCompleted} jobs completed
                                                </p>
                                            </div>
                                            <div className="flex items-center text-yellow-600">
                                                <Star className="h-4 w-4 fill-yellow-400" />
                                                <span className="ml-1 font-medium">{vendor.rating?.overall?.toFixed(1)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Spending Overview */}
                    <Card className="dark:bg-slate-900 dark:border-slate-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center space-x-2">
                                <DollarSign className="h-5 w-5 text-green-600" />
                                <span>Spending Overview</span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-center py-4">
                                <p className="text-3xl font-bold text-gray-900">
                                    ${metrics.monthlySpend.toLocaleString()}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">Total Vendor Revenue</p>
                                <div className={`flex items-center justify-center mt-2 ${metrics.monthlySpendChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {metrics.monthlySpendChange >= 0 ? (
                                        <TrendingUp className="h-4 w-4 mr-1" />
                                    ) : (
                                        <TrendingDown className="h-4 w-4 mr-1" />
                                    )}
                                    <span className="text-sm font-medium">
                                        {Math.abs(metrics.monthlySpendChange)}% from last period
                                    </span>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                className="w-full mt-4"
                                onClick={() => onNavigate?.('payments')}
                            >
                                <FileText className="h-4 w-4 mr-2" />
                                View Payment Details
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Recent Activity */}
                    <Card className="dark:bg-slate-900 dark:border-slate-800">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center space-x-2">
                                <MessageSquare className="h-5 w-5 text-blue-600" />
                                <span>Recent Activity</span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {recentActivity.length === 0 ? (
                                <div className="text-center py-8">
                                    <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                    <p className="text-gray-600">No recent activity</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {recentActivity.slice(0, 5).map(activity => (
                                        <div key={activity.id} className="flex items-start space-x-3 text-sm">
                                            <div className="flex-1">
                                                <p className="text-gray-900">{activity.description}</p>
                                                <p className="text-gray-500">
                                                    {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
